import { useParams, useNavigate } from 'react-router-dom';
import { Descriptions, Card, Button, Space, Spin, Table, Tag, Alert, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { PageHeader } from '../../components/common/PageHeader';
import { StatusBadge } from '../../components/common/StatusBadge';
import { ErrorResult } from '../../components/common/ErrorResult';
import { confirmAction } from '../../components/common/ConfirmAction';
import { useInvoiceDetail, useReprocessInvoice, useDeleteInvoice } from '../../hooks/useInvoices';
import type { AdminLineItemDetail } from '../../types/invoices';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const formatCurrency = (value?: number | null, currency?: string | null) => {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(value);
};

const formatBytes = (bytes?: number | null) => {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const lineItemColumns: ColumnsType<AdminLineItemDetail> = [
  {
    title: 'Description',
    dataIndex: 'description',
    key: 'description',
    render: (v: string | null) => v || '—',
  },
  {
    title: 'SKU',
    dataIndex: 'sku',
    key: 'sku',
    width: 120,
    render: (v: string | null) => v || '—',
  },
  {
    title: 'Qty',
    dataIndex: 'quantity',
    key: 'quantity',
    width: 80,
    align: 'right' as const,
    render: (v: number | null) => v ?? '—',
  },
  {
    title: 'Unit Price',
    dataIndex: 'unit_price',
    key: 'unit_price',
    width: 120,
    align: 'right' as const,
    render: (v: number | null) => (v != null ? `$${Number(v).toFixed(2)}` : '—'),
  },
  {
    title: 'Total',
    dataIndex: 'line_total',
    key: 'line_total',
    width: 120,
    align: 'right' as const,
    render: (v: number | null) => (v != null ? `$${Number(v).toFixed(2)}` : '—'),
  },
];

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: inv, isLoading, isError, refetch } = useInvoiceDetail(id!);
  const reprocess = useReprocessInvoice();
  const deleteInvoice = useDeleteInvoice();

  if (isLoading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  if (isError || !inv) return <ErrorResult onRetry={refetch} />;

  const ext = inv.extracted;

  return (
    <>
      <PageHeader
        title={inv.original_filename}
        extra={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/invoices')}>
              Back
            </Button>
            <Button type="primary" loading={reprocess.isPending} onClick={() => reprocess.mutate(id!)}>
              Reprocess
            </Button>
            <Button
              danger
              onClick={() =>
                confirmAction({
                  title: 'Delete Invoice',
                  content: `Delete ${inv.original_filename}?`,
                  onConfirm: () => {
                    deleteInvoice.mutate(id!);
                    navigate('/invoices');
                  },
                  danger: true,
                })
              }
            >
              Delete
            </Button>
          </Space>
        }
      />

      {/* Metadata */}
      <Card>
        <Descriptions column={3}>
          <Descriptions.Item label="Status">
            <StatusBadge status={inv.status} />
          </Descriptions.Item>
          <Descriptions.Item label="Organization">{inv.organization_name}</Descriptions.Item>
          <Descriptions.Item label="File Size">{formatBytes(inv.file_size_bytes)}</Descriptions.Item>
          <Descriptions.Item label="MIME Type">{inv.mime_type || '—'}</Descriptions.Item>
          <Descriptions.Item label="Created">
            {dayjs(inv.created_at).format('MMM D, YYYY HH:mm')}
          </Descriptions.Item>
          <Descriptions.Item label="Updated">
            {dayjs(inv.updated_at).format('MMM D, YYYY HH:mm')}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Error message for failed invoices */}
      {inv.status === 'failed' && inv.error_message && (
        <Alert
          type="error"
          showIcon
          message="Processing Failed"
          description={inv.error_message}
          style={{ marginTop: 16 }}
        />
      )}

      {/* Extracted Data */}
      {ext && (
        <Card title="Extracted Data" style={{ marginTop: 16 }}>
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Vendor">{ext.vendor_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Invoice Number">{ext.invoice_number || '—'}</Descriptions.Item>
            <Descriptions.Item label="Invoice Date">
              {ext.invoice_date ? dayjs(ext.invoice_date).format('MMM D, YYYY') : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Due Date">
              {ext.due_date ? dayjs(ext.due_date).format('MMM D, YYYY') : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Customer">{ext.customer_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Payment Terms">{ext.payment_terms || '—'}</Descriptions.Item>
            <Descriptions.Item label="Subtotal">
              {formatCurrency(ext.subtotal, ext.currency_code)}
            </Descriptions.Item>
            <Descriptions.Item label="Tax">
              {formatCurrency(ext.tax_amount, ext.currency_code)}
            </Descriptions.Item>
            <Descriptions.Item label="Total Amount">
              <Typography.Text strong>
                {formatCurrency(ext.total_amount, ext.currency_code)}
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="Confidence">
              {ext.confidence_score != null ? (
                <Tag color={ext.confidence_score >= 0.9 ? 'green' : ext.confidence_score >= 0.7 ? 'orange' : 'red'}>
                  {(ext.confidence_score * 100).toFixed(1)}%
                </Tag>
              ) : (
                '—'
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {/* Line Items */}
      {ext && ext.line_items.length > 0 && (
        <Card title="Line Items" style={{ marginTop: 16 }}>
          <Table<AdminLineItemDetail>
            columns={lineItemColumns}
            dataSource={ext.line_items}
            rowKey={(_, i) => String(i)}
            pagination={false}
            size="small"
          />
        </Card>
      )}
    </>
  );
}
